import {
  Activity,
  ArrowLeftRight,
  Banknote,
  Bell,
  Briefcase,
  Building2,
  CheckCheck,
  Command,
  CreditCard,
  FileSpreadsheet,
  Eye,
  FileText,
  Fingerprint,
  KeyRound,
  Landmark,
  Percent,
  ShieldCheck,
  ScrollText,
  Send,
  Shield,
  SlidersHorizontal,
  StickyNote,
  Users,
  Wallet,
  Webhook,
  type LucideIcon,
} from "lucide-react";

import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import type { PermissionModule } from "@/shared/types/permission.types";

import type { AtomicAction, InvitationLanguage } from "../types/access.types";

/**
 * The real permission catalog. Source of truth = backend `Module` enum (`permission.interface.ts`) × the
 * atomic actions `read/write/delete` (`ATOMIC_ACTIONS`). This deliberately replaces the design mock's demo
 * catalog (Wallets/Payments/Beneficiaries/Billing = 38 actions), which does NOT match the backend. The
 * "Acceso efectivo X/total" denominator is derived from here.
 */

/** Atomic actions, in display order. Every module exposes these three. */
export const ATOMIC_ACTIONS: readonly AtomicAction[] = ["read", "write", "delete"];

/** RBAC endpoints. Base URL (`VITE_COLOCA_API`) already includes `/api/v1`. */
export const ACCESS_ENDPOINTS = {
  ROLES: "/internal/roles",
  roleById: (id: string) => `/internal/roles/${id}`,
  POLICIES: "/internal/policies",
  policyById: (id: string) => `/internal/policies/${id}`,
  userRoles: (userId: string) => `/internal/users/${userId}/roles`,
  userRole: (userId: string, roleId: string) => `/internal/users/${userId}/roles/${roleId}`,
  INVITATIONS: "/internal/invitations",
  apiKeyRoles: (keyId: string) => `/api-keys/${keyId}/roles`,
  apiKeyRole: (keyId: string, roleId: string) => `/api-keys/${keyId}/roles/${roleId}`,
} as const;

/**
 * Rows per page in the access table. 25 is the house standard (payments-activity, clients, history), and the
 * whole page now costs ONE request: the listing carries its roles via `?include=roles`.
 */
export const ACCESS_USERS_PAGE_SIZE = 25;

/**
 * Shortest term the users search will send. Mirrors the backend's `USER_SEARCH_MIN_LENGTH`: below three
 * characters `pg_trgm` has no trigram to look up, so the API rejects it with a 400 rather than serve a
 * sequential scan. Typing one or two characters must therefore NOT reach the wire.
 */
export const ACCESS_SEARCH_MIN_LENGTH = 3;

/** Longest term the API accepts (`@MaxLength(100)`); the input caps at this so a paste cannot 400. */
export const ACCESS_SEARCH_MAX_LENGTH = 100;

/** House standard for search debounce — same as clients, audit and cards. */
export const ACCESS_SEARCH_DEBOUNCE_MS = 350;

/** Languages the invitation email supports, in display order (mirror of `CreateInvitationDto`). */
export const INVITATION_LANGUAGES = ["es", "en", "pt"] as const satisfies readonly InvitationLanguage[];

/**
 * How many API keys to fetch for the access table. Unlike the users listing — which now pages server-side
 * with {@link ACCESS_USERS_PAGE_SIZE} and carries its roles inline — this is still a deliberate cap, not full
 * pagination: each key costs a per-principal `GET /api-keys/:id/roles` call (fanned out in parallel), so
 * loading the whole set would multiply the role requests. The backend page is honest about truncation via its
 * `total`, which the API Keys section surfaces when more keys exist than are shown.
 */
export const ACCESS_API_KEYS_LIMIT = 50;

type AccessModuleLabelKey = Extract<keyof SettingsTranslations, `access_mod_${string}`>;

export interface AccessCatalogModule {
  /**
   * A backend `Module` value. Typed against {@link PermissionModule} so a renamed or invented id fails the
   * build instead of silently rendering a row nobody can hold.
   */
  id: PermissionModule;
  labelKey: AccessModuleLabelKey;
  icon: LucideIcon;
  /** Atomic actions shown for this module (always the three atoms). */
  actions: readonly AtomicAction[];
  /** Atoms flagged as high-impact — rendered with a warning cue. */
  risky?: Partial<Record<AtomicAction, boolean>>;
}

const WRITE_DELETE_RISKY: Partial<Record<AtomicAction, boolean>> = { write: true, delete: true };
const DELETE_RISKY: Partial<Record<AtomicAction, boolean>> = { delete: true };

/**
 * The 31 backend modules. Each renders a row in the effective-access matrix with read/write/delete cells (31
 * × 3 = 93 total cells = the "X/total" denominator). Friendly Spanish labels live in i18n (`access_mod_*`) to
 * keep the design's look while the resource ids stay backend-accurate.
 *
 * `payments` used to be missing, so the matrix showed 27 rows / 81 cells and an admin could not audit who
 * reached the payments runtime — the module behind wallets, payins, payouts, swaps and payments-config. A
 * test asserts this list covers `PERMISSION_MODULES` so the gap cannot reopen.
 */
export const ACCESS_CATALOG: readonly AccessCatalogModule[] = [
  {
    id: "users",
    labelKey: "access_mod_users",
    icon: Users,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "api_key",
    labelKey: "access_mod_api_key",
    icon: KeyRound,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "roles",
    labelKey: "access_mod_roles",
    icon: Shield,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "policies",
    labelKey: "access_mod_policies",
    icon: Command,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "organizations",
    labelKey: "access_mod_organizations",
    icon: Building2,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "organization_accounts",
    labelKey: "access_mod_organization_accounts",
    icon: Landmark,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "identities",
    labelKey: "access_mod_identities",
    icon: Fingerprint,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "wallets",
    labelKey: "access_mod_wallets",
    icon: Wallet,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "wallets:transfer:sensitive",
    labelKey: "access_mod_wallets_transfer_sensitive",
    icon: ArrowLeftRight,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "cards",
    labelKey: "access_mod_cards",
    icon: CreditCard,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "cards:topup",
    labelKey: "access_mod_cards_topup",
    icon: Wallet,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "cards:transfer",
    labelKey: "access_mod_cards_transfer",
    icon: ArrowLeftRight,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "cards:sensitive",
    labelKey: "access_mod_cards_sensitive",
    icon: Eye,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "cards:topup:sensitive",
    labelKey: "access_mod_cards_topup_sensitive",
    icon: Eye,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  { id: "fees", labelKey: "access_mod_fees", icon: Percent, actions: ATOMIC_ACTIONS, risky: DELETE_RISKY },
  {
    id: "fee_configurations",
    labelKey: "access_mod_fee_configurations",
    icon: SlidersHorizontal,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    // The payments runtime: wallets, payins, payouts, swaps, quotes and payments-config all authorize
    // on this module. `write` moves money, so both non-read atoms are flagged.
    id: "payments",
    labelKey: "access_mod_payments",
    icon: Banknote,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "payout_batches",
    labelKey: "access_mod_payout_batches",
    icon: FileSpreadsheet,
    actions: ATOMIC_ACTIONS,
    // `write` uploads a spreadsheet of payouts and curates its selection; `delete` is unused today.
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "payout_batches:approve",
    labelKey: "access_mod_payout_batches_approve",
    icon: ShieldCheck,
    actions: ATOMIC_ACTIONS,
    // `write` executes, retries and resumes a batch: it moves money. Never on the uploader's own batch.
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "clients",
    labelKey: "access_mod_clients",
    icon: Briefcase,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "client_accounts",
    labelKey: "access_mod_client_accounts",
    icon: Landmark,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations",
    labelKey: "access_mod_operations",
    icon: Activity,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations:payments",
    labelKey: "access_mod_operations_payments",
    icon: Send,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "operations:confirmations",
    labelKey: "access_mod_operations_confirmations",
    icon: CheckCheck,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "operations:notes",
    labelKey: "access_mod_operations_notes",
    icon: StickyNote,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations:shipping_quotes",
    labelKey: "access_mod_operations_shipping_quotes",
    icon: FileText,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations:shipping_quotes_coloca",
    labelKey: "access_mod_operations_shipping_quotes_coloca",
    icon: FileText,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations:audit",
    labelKey: "access_mod_operations_audit",
    icon: ScrollText,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "operations:public_accounts",
    labelKey: "access_mod_operations_public_accounts",
    icon: Landmark,
    actions: ATOMIC_ACTIONS,
    risky: WRITE_DELETE_RISKY,
  },
  {
    id: "operations:notifications",
    labelKey: "access_mod_operations_notifications",
    icon: Bell,
    actions: ATOMIC_ACTIONS,
    risky: DELETE_RISKY,
  },
  {
    id: "organization_webhooks",
    labelKey: "access_mod_organization_webhooks",
    icon: Webhook,
    actions: ATOMIC_ACTIONS,
    // `write` rotates the secret and re-points the endpoint; `delete` stops every delivery.
    risky: WRITE_DELETE_RISKY,
  },
];

/** Total `(module, atom)` cells — the "Acceso efectivo X/total" denominator. */
export const TOTAL_CELLS = ACCESS_CATALOG.reduce((sum, mod) => sum + mod.actions.length, 0);

/**
 * Deterministic role color palette. The backend does not expose a per-role color, so we derive one from the
 * role `slug` (see `utils/roleColor.ts`). Used as inline `background` on the role dot — a data-driven value,
 * not a class.
 */
export const ROLE_COLOR_PALETTE: readonly string[] = [
  "#00735C",
  "#6366F1",
  "#D97706",
  "#0EA5E9",
  "#DB2777",
  "#16A34A",
  "#64748B",
  "#EA580C",
];
